<template>
  <div
    ref="root"
    class="input emoji-input"
    :class="{ '-with-picker': !hideEmojiButton, '-textarea': input?.tagName === 'TEXTAREA' || richInput }"
  >
    <slot
      :id="'textbox-' + randomSeed"
      :aria-owns="suggestionListId"
      aria-autocomplete="both"
      :aria-expanded="showSuggestions"
      :aria-activedescendant="(!showSuggestions || highlighted === -1) ? '' : suggestionItemId(highlighted)"
    />
    <!-- TODO: make the 'x' disappear if at the end maybe? -->
    <div
      ref="hiddenOverlay"
      class="hidden-overlay"
      :style="overlayStyle"
      :aria-hidden="true"
    >
      <span v-if="!richInput">{{ preText }}</span>
      <span
        v-else
        ref="overlayPre"
      />
      <span
        ref="hiddenOverlayCaret"
        class="caret"
      >x</span>
      <span>{{ postText }}</span>
    </div>
    <screen-reader-notice
      ref="screenReaderNotice"
      aria-live="assertive"
    />
    <template v-if="enableEmojiPicker">
      <button
        v-if="!hideEmojiButton"
        class="button-unstyled emoji-picker-icon"
        type="button"
        :title="$t('emoji.add_emoji')"
        @click.prevent="togglePicker"
      >
        <FAIcon :icon="['far', 'face-smile-beam']" />
      </button>
      <EmojiPicker
        v-if="enableEmojiPicker"
        ref="picker"
        :enable-sticker-picker="enableStickerPicker"
        :quick="quickPicker"
        :align="quickPicker ? 'start' : undefined"
        :bound-to-selector="pickerBoundTo"
        class="emoji-picker-panel"
        @emoji="insert"
        @sticker-uploaded="onStickerUploaded"
        @sticker-upload-failed="onStickerUploadFailed"
        @show="onPickerShown"
        @close="onPickerClosed"
      />
    </template>
    <Popover
      ref="suggestorPopover"
      class="autocomplete-panel"
      placement="bottom"
      :hide-trigger="true"
    >
      <template #content>
        <div
          :id="suggestionListId"
          ref="panel-body"
          class="autocomplete-panel-body"
          role="listbox"
        >
          <div
            v-for="(suggestion, index) in suggestions"
            :id="suggestionItemId(index)"
            :key="index"
            class="menu-item autocomplete-item"
            role="option"
            :class="{ '-active': index === highlighted }"
            :aria-label="autoCompleteItemLabel(suggestion)"
            :aria-selected="index === highlighted"
            @click.stop.prevent="onClick($event, suggestion)"
          >
            <span
              class="image"
              :class="{ '-user': suggestion.user }"
            >
              <img
                v-if="suggestion.img"
                :src="suggestion.img"
              >
              <UnicodeEmoji
                v-else
                :emoji="suggestion.replacement"
              />
            </span>
            <!-- a user: the name, then the handle; an emoji: its name -->
            <div
              v-if="suggestion.user"
              class="label"
            >
              <span class="displayText -name">{{ suggestion.detailText || suggestion.displayText }}</span>
              <span class="detailText">
                {{ suggestion.displayText }}<UnicodeDomainIndicator
                  :user="suggestion.user"
                  :at="false"
                />
              </span>
            </div>
            <div
              v-else
              class="label"
            >
              <span class="displayText">{{ maybeLocalizedEmojiName(suggestion) }}</span>
              <span
                v-if="suggestion.detailText"
                class="detailText"
              >{{ suggestion.detailText }}</span>
            </div>
          </div>
        </div>
      </template>
    </Popover>
  </div>
</template>

<script src="./emoji_input.js"></script>

<style lang="scss">
.input.emoji-input {
  padding: 0;
  display: flex;
  flex-direction: column;
  position: relative;

  .emoji-picker-icon {
    position: absolute;
    bottom: 0;
    right: 0;
    height: 100%;
    padding: 0 0.2em;
    font-size: var(--fs-xl);
    cursor: pointer;
    line-height: 1.2em;

    &:hover i {
      color: var(--text);
    }
  }

  &.-textarea {
    .emoji-picker-icon {
      height: auto;
      padding: 0.2em;
    }
  }

  .emoji-picker-panel {
    position: absolute;
    z-index: 20;
    margin-top: 0.2em;

    &.hide {
      display: none;
    }
  }

  input,
  textarea {
    flex: 1 0 auto;
    color: inherit;
    /* stylelint-disable-next-line declaration-no-important */
    background: none !important;
    box-shadow: none;
    border: none;
    outline: none;
  }

  &.-with-picker {
    textarea,
    input {
      padding-right: 2.4em;
    }
  }

  .hidden-overlay {
    opacity: 0;
    pointer-events: none;
    position: absolute;
    inset: 0;
    overflow: hidden;

    /* DEBUG STUFF */
    color: red;

    /* set opacity to non-zero to see the overlay */

    .caret {
      width: 0;
      margin-right: calc(-1ch - 1px);
      border: 1px solid red;
    }
  }
}
</style>

<style lang="scss" src="./autocomplete.scss"></style>
